<template>
    <div class="glass-container">
        <div class="state-customers-container">
            <div class="modern-card">
                <!-- Header -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Customers by State</h1>
                        <p class="page-subtitle">Distribution of customers and revenue across states</p>
                    </div>
                    
                    <!-- Chart Controls -->
                    <div class="chart-controls">
                        <div class="control-group">
                            <label class="control-label">Chart Type</label>
                            <b-form-select 
                                v-model="chartType" 
                                :options="chartTypeOptions"
                                size="sm"
                                class="glass-select"
                            ></b-form-select>
                        </div>
                    </div>
                </div>

                <!-- Main Content -->
                <div class="main-content">
                    <b-row>
                        <!-- Chart Column -->
                        <b-col lg="6" class="mb-4 mb-lg-0">
                            <div class="chart-container-glass">
                                <div class="chart-header">
                                    <h3 class="chart-title">
                                        {{ chartType === 'bar' ? 'Customers by State' : 'Revenue by State' }}
                                    </h3>
                                    <div class="chart-legend">
                                        <div class="legend-item">
                                            <span class="legend-color customers"></span>
                                            <span class="legend-label">Customers</span>
                                        </div>
                                        <div class="legend-item">
                                            <span class="legend-color revenue"></span>
                                            <span class="legend-label">Revenue</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="chart-wrapper">
                                    <canvas id="stateChart" ref="stateChart" height="350"></canvas>
                                </div>
                                
                                <div class="chart-footer">
                                    <div class="stats-summary">
                                        <div class="stat-summary">
                                            <b-icon icon="geo-alt" class="mr-2"></b-icon>
                                            <span class="stat-label">States:</span>
                                            <span class="stat-value">{{ stateData.length }}</span>
                                        </div>
                                        <div class="stat-summary">
                                            <b-icon icon="people" class="mr-2"></b-icon>
                                            <span class="stat-label">Total Customers:</span>
                                            <span class="stat-value">{{ totalCustomers.toLocaleString() }}</span>
                                        </div>
                                        <div class="stat-summary">
                                            <b-icon icon="cash" class="mr-2"></b-icon>
                                            <span class="stat-label">Total Revenue:</span>
                                            <span class="stat-value">${{ totalRevenue.toLocaleString() }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </b-col>

                        <!-- Table Column -->
                        <b-col lg="6">
                            <div class="table-container-glass">
                                <div class="table-header-section">
                                    <h3 class="table-title">State Performance Details</h3>
                                    <div class="table-controls">
                                        <b-form-input
                                            v-model="stateSearch"
                                            placeholder="Search state..."
                                            size="sm"
                                            class="glass-input"
                                        ></b-form-input>
                                    </div>
                                </div>
                                
                                <div class="table-responsive">
                                    <table class="modern-table">
                                        <thead>
                                            <tr>
                                                <th 
                                                    v-for="field in stateFields" 
                                                    :key="field.key"
                                                    class="table-header"
                                                    @click="sortBy = field.key; sortDesc = !sortDesc"
                                                >
                                                    {{ field.label }}
                                                    <span class="sort-indicator" v-if="sortBy === field.key">
                                                        {{ sortDesc ? '↓' : '↑' }}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr 
                                                v-for="(state, index) in filteredStateData" 
                                                :key="index"
                                                class="table-row"
                                                :class="getStateRowClass(state)"
                                            >
                                                <td class="table-cell">
                                                    <div class="state-cell">
                                                        <b-icon icon="flag" class="state-icon"></b-icon>
                                                        <span class="state-code">{{ state.state }}</span>
                                                        <span class="state-name">{{ getStateName(state.state) }}</span>
                                                    </div>
                                                </td>
                                                
                                                <td class="table-cell">
                                                    <div class="customer-count">
                                                        <b-icon icon="person" class="mr-1"></b-icon>
                                                        {{ state.customer_count.toLocaleString() }}
                                                    </div>
                                                </td>
                                                
                                                <td class="table-cell">
                                                    <div class="orders-count">
                                                        {{ state.total_orders.toLocaleString() }}
                                                    </div>
                                                </td>
                                                
                                                <td class="table-cell">
                                                    <div class="revenue-cell">
                                                        <b-icon icon="currency-dollar" class="mr-1"></b-icon>
                                                        ${{ state.total_revenue.toLocaleString() }}
                                                    </div>
                                                </td>
                                                
                                                <td class="table-cell">
                                                    <div class="aov-cell">
                                                        <b-badge :variant="getAOVVariant(state.avg_order_value)" class="glass-badge">
                                                            ${{ state.avg_order_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                                        </b-badge>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                
                                <!-- Table Footer -->
                                <div class="table-footer" v-if="filteredStateData.length > 0">
                                    <div class="pagination-info">
                                        Showing {{ filteredStateData.length }} of {{ stateData.length }} states
                                        <span v-if="stateSearch"> (filtered)</span>
                                    </div>
                                </div>
                                
                                <!-- Empty State -->
                                <div v-else class="empty-state">
                                    <div class="empty-state-icon">
                                        <b-icon icon="search" font-scale="3"></b-icon>
                                    </div>
                                    <h4>No States Found</h4>
                                    <p>Try adjusting your search</p>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js';

export default {
    name: 'StateWiseCustomers',
    props: {
        locationData: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            sortBy: 'customer_count',
            sortDesc: true,
            stateSearch: '',
            chartType: 'bar',
            chart: null,
            
            stateFields: [
                { key: 'state', label: 'State', sortable: true },
                { key: 'customer_count', label: 'Customers', sortable: true },
                { key: 'total_orders', label: 'Total Orders', sortable: true },
                { key: 'total_revenue', label: 'Total Revenue', sortable: true },
                { key: 'avg_order_value', label: 'Avg Order Value', sortable: true }
            ],
            
            chartTypeOptions: [
                { value: 'bar', text: 'Bar Chart' },
                { value: 'line', text: 'Line Chart' }
            ],
            
            stateNames: {
                'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
                'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
                'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
                'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
                'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
                'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
                'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
                'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
                'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
            }
        };
    },
    computed: {
        stateData() {
            const stateMap = new Map();
            
            this.locationData.forEach(location => {
                if (location.state) {
                    const state = location.state.toUpperCase();
                    if (!stateMap.has(state)) {
                        stateMap.set(state, {
                            state: state,
                            customer_count: 0,
                            total_orders: 0,
                            total_revenue: 0,
                            avg_order_value: 0
                        });
                    }
                    
                    const stateData = stateMap.get(state);
                    stateData.customer_count += location.customer_count || 0;
                    stateData.total_orders += location.total_orders || 0;
                    stateData.total_revenue += location.total_revenue || 0;
                }
            });
            
            // Calculate averages
            stateMap.forEach(stateData => {
                stateData.avg_order_value = stateData.total_orders > 0 ? 
                    stateData.total_revenue / stateData.total_orders : 0;
            });
            
            return Array.from(stateMap.values());
        },
        
        sortedStateData() {
            const sorted = [...this.stateData];
            
            if (this.sortBy === 'customer_count') {
                sorted.sort((a, b) => {
                    return this.sortDesc ? b.customer_count - a.customer_count : a.customer_count - b.customer_count;
                });
            } else if (this.sortBy === 'total_revenue') {
                sorted.sort((a, b) => {
                    return this.sortDesc ? b.total_revenue - a.total_revenue : a.total_revenue - b.total_revenue;
                });
            } else if (this.sortBy === 'avg_order_value') {
                sorted.sort((a, b) => {
                    return this.sortDesc ? b.avg_order_value - a.avg_order_value : a.avg_order_value - b.avg_order_value;
                });
            } else if (this.sortBy === 'total_orders') {
                sorted.sort((a, b) => {
                    return this.sortDesc ? b.total_orders - a.total_orders : a.total_orders - b.total_orders;
                });
            } else if (this.sortBy === 'state') {
                sorted.sort((a, b) => {
                    if (this.sortDesc) {
                        return b.state.localeCompare(a.state);
                    }
                    return a.state.localeCompare(b.state);
                });
            }
            
            return sorted;
        },
        
        filteredStateData() {
            if (!this.stateSearch) return this.sortedStateData;
            
            const searchTerm = this.stateSearch.toLowerCase();
            return this.sortedStateData.filter(state => 
                state.state.toLowerCase().includes(searchTerm) ||
                this.getStateName(state.state).toLowerCase().includes(searchTerm)
            );
        },
        
        totalCustomers() {
            return this.stateData.reduce((sum, state) => sum + state.customer_count, 0);
        },
        
        totalRevenue() {
            return this.stateData.reduce((sum, state) => sum + state.total_revenue, 0);
        },
        
        maxCustomers() {
            return Math.max(...this.stateData.map(s => s.customer_count), 1);
        },
        
        maxRevenue() {
            return Math.max(...this.stateData.map(s => s.total_revenue), 1);
        }
    },
    watch: {
        chartType() {
            this.renderChart();
        },
        stateData: {
            handler() {
                if (this.chart) {
                    this.chart.destroy();
                }
                this.renderChart();
            },
            deep: true
        }
    },
    mounted() {
        this.renderChart();
    },
    methods: {
        renderChart() {
            if (!this.stateData.length || !this.$refs.stateChart) return;
            
            const ctx = this.$refs.stateChart.getContext('2d');
            const sortedData = [...this.stateData].sort((a, b) => b.customer_count - a.customer_count);
            const labels = sortedData.slice(0, 15).map(s => s.state);
            const data = sortedData.slice(0, 15).map(s => s.customer_count);
            const revenueData = sortedData.slice(0, 15).map(s => s.total_revenue);
            
            // Destroy existing chart
            if (this.chart) {
                this.chart.destroy();
            }
            
            // Create gradient for bar charts
            const gradient1 = ctx.createLinearGradient(0, 0, 0, 400);
            gradient1.addColorStop(0, 'rgba(66, 153, 225, 0.8)');
            gradient1.addColorStop(1, 'rgba(66, 153, 225, 0.2)');
            
            const gradient2 = ctx.createLinearGradient(0, 0, 0, 400);
            gradient2.addColorStop(0, 'rgba(102, 126, 234, 0.8)');
            gradient2.addColorStop(1, 'rgba(102, 126, 234, 0.2)');
            
            this.chart = new Chart(ctx, {
                type: this.chartType === 'bar' ? 'bar' : 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Customer Count',
                            data: data,
                            backgroundColor: this.chartType === 'bar' ? gradient1 : 'rgba(66, 153, 225, 0.1)',
                            borderColor: 'rgba(66, 153, 225, 1)',
                            borderWidth: 2,
                            borderDash: this.chartType === 'line' ? [5, 5] : [],
                            fill: true,
                            tension: 0.4,
                            pointBackgroundColor: 'rgba(66, 153, 225, 1)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 5,
                            pointHoverRadius: 7
                        },
                        {
                            label: 'Total Revenue',
                            data: revenueData,
                            backgroundColor: this.chartType === 'bar' ? gradient2 : 'rgba(102, 126, 234, 0.1)',
                            borderColor: 'rgba(102, 126, 234, 1)',
                            borderWidth: 2,
                            borderDash: [],
                            fill: true,
                            tension: 0.4,
                            pointBackgroundColor: 'rgba(102, 126, 234, 1)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 5,
                            pointHoverRadius: 7
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false
                    },
                    plugins: {
                        legend: {
                            display: false
                        },
                        tooltip: {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            titleColor: '#2d3748',
                            bodyColor: '#4a5568',
                            borderColor: 'rgba(226, 232, 240, 0.5)',
                            borderWidth: 1,
                            padding: 12,
                            boxPadding: 6,
                            usePointStyle: true,
                            callbacks: {
                                label: function(context) {
                                    let label = context.dataset.label || '';
                                    if (label) {
                                        label += ': ';
                                    }
                                    if (context.datasetIndex === 0) {
                                        label += context.parsed.y.toLocaleString() + ' customers';
                                    } else {
                                        label += '$' + context.parsed.y.toLocaleString();
                                    }
                                    return label;
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: {
                                color: 'rgba(226, 232, 240, 0.5)',
                                drawBorder: false
                            },
                            ticks: {
                                color: '#718096',
                                font: {
                                    size: 11
                                }
                            }
                        },
                        y: {
                            beginAtZero: true,
                            grid: {
                                color: 'rgba(226, 232, 240, 0.3)',
                                drawBorder: false
                            },
                            ticks: {
                                color: '#718096',
                                callback: function(value) {
                                    if (value >= 1000) {
                                        return (value / 1000).toFixed(0) + 'k';
                                    }
                                    return value;
                                }
                            },
                            title: {
                                display: true,
                                text: 'Customer Count',
                                color: '#4a5568',
                                font: {
                                    size: 12,
                                    weight: '600'
                                }
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            grid: {
                                drawOnChartArea: false
                            },
                            ticks: {
                                color: '#718096',
                                callback: function(value) {
                                    if (value >= 1000000) {
                                        return '$' + (value / 1000000).toFixed(1) + 'M';
                                    }
                                    if (value >= 1000) {
                                        return '$' + (value / 1000).toFixed(0) + 'k';
                                    }
                                    return '$' + value;
                                }
                            },
                            title: {
                                display: true,
                                text: 'Revenue ($)',
                                color: '#4a5568',
                                font: {
                                    size: 12,
                                    weight: '600'
                                }
                            }
                        }
                    },
                    animation: {
                        duration: 1000,
                        easing: 'easeOutQuart'
                    }
                }
            });
        },
        
        getStateName(stateCode) {
            return this.stateNames[stateCode] || stateCode;
        },
        
        getAOVVariant(aov) {
            if (aov > 2000) return 'success';
            if (aov > 1000) return 'warning';
            return 'danger';
        },
        
        getStateRowClass(state) {
            const rank = this.getStateRank(state.state);
            if (rank <= 3) return 'row-top-ranked';
            if (rank <= 10) return 'row-high-ranked';
            return 'row-normal-ranked';
        },
        
        getStateRank(stateCode) {
            const sorted = [...this.stateData].sort((a, b) => b.customer_count - a.customer_count);
            const index = sorted.findIndex(s => s.state === stateCode);
            return index + 1;
        }
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
    }
}
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    z-index: 0;
}

.state-customers-container {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    border-radius: 34px;
}

.modern-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    box-shadow: 
        0 8px 32px rgba(31, 38, 135, 0.2),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    padding: 30px;
    margin-top: 20px;
    animation: cardAppear 0.6s ease-out;
    position: relative;
    overflow: hidden;
}

.modern-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.8) 50%, 
        transparent 100%);
}

/* Header Section */
.header-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    position: relative;
}

.header-section::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 2px;
    animation: slideIn 0.6s ease-out;
}

.title-section {
    animation: slideInUp 0.5s ease-out;
}

.page-title {
    font-size: 2.2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #364050 0%, #364050 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
    text-shadow: 0 2px 10px rgba(102, 126, 234, 0.1);
}

.page-subtitle {
    color: rgba(108, 117, 125, 0.9);
    font-size: 1rem;
    font-weight: 400;
}

.chart-controls {
    animation: slideInDown 0.5s ease-out;
}

.control-group {
    display: flex;
    align-items: center;
    gap: 15px;
}

.control-label {
    color: #4a5568;
    font-weight: 600;
    font-size: 0.9rem;
    margin: 0;
}

.glass-select {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 8px;
    color: #4a5568;
    min-width: 150px;
}

.glass-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Main Content */
.main-content {
    animation: fadeIn 0.6s ease-out 0.2s both;
}

/* Chart Container Glass Effect */
.chart-container-glass {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 25px;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    height: 100%;
    min-height: 500px;
    display: flex;
    flex-direction: column;
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.chart-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.chart-legend {
    display: flex;
    gap: 20px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.legend-color {
    width: 12px;
    height: 12px;
    border-radius: 3px;
}

.legend-color.customers {
    background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
}

.legend-color.revenue {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.legend-label {
    color: #718096;
    font-size: 0.85rem;
    font-weight: 500;
}

.chart-wrapper {
    flex: 1;
    position: relative;
    margin: 15px 0;
}

.chart-footer {
    margin-top: 25px;
    padding-top: 20px;
    border-top: 1px solid rgba(226, 232, 240, 0.5);
}

.stats-summary {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
}

.stat-summary {
    display: flex;
    align-items: center;
    padding: 8px 15px;
    background: rgba(226, 232, 240, 0.3);
    border-radius: 10px;
    border: 1px solid rgba(226, 232, 240, 0.5);
}

.stat-label {
    color: #718096;
    font-size: 0.85rem;
    margin: 0 8px;
}

.stat-value {
    color: #2d3748;
    font-weight: 600;
    font-size: 1.1rem;
}

/* Table Container Glass Effect */
.table-container-glass {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    height: 100%;
    display: flex;
    flex-direction: column;
}

.table-header-section {
    padding: 20px 25px 15px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.table-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 15px;
}

.table-controls {
    display: flex;
    justify-content: flex-end;
}

.glass-input {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 8px;
    color: #4a5568;
    max-width: 200px;
}

.glass-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Table Styles */
.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.table-header {
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.1) 0%, 
        rgba(118, 75, 162, 0.1) 100%);
    padding: 15px 20px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid rgba(226, 232, 240, 0.5);
    position: relative;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
    white-space: nowrap;
}

.table-header:hover {
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.15) 0%, 
        rgba(118, 75, 162, 0.15) 100%);
}

.table-header::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    transition: width 0.3s ease;
}

.table-header:hover::after {
    width: 100%;
}

.sort-indicator {
    margin-left: 5px;
    font-weight: bold;
    color: #667eea;
}

.table-row {
    border-bottom: 1px solid rgba(226, 232, 240, 0.3);
    transition: all 0.3s ease;
    position: relative;
}

.table-row:hover {
    background: rgba(102, 126, 234, 0.05);
    transform: translateX(4px);
}

/* .table-row::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(to bottom, transparent, #667eea, transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
} */

.table-row:hover::before {
    opacity: 1;
}

.table-row.row-top-ranked {
    background: linear-gradient(90deg, rgba(56, 161, 105, 0.08), transparent);
}

.table-row.row-high-ranked {
    background: linear-gradient(90deg, rgba(237, 137, 54, 0.08), transparent);
}

.table-row.row-normal-ranked {
    background: transparent;
}

.table-cell {
    padding: 12px 20px;
    vertical-align: middle;
    color: #4a5568;
    font-size: 0.9rem;
}

/* State Cell */
.state-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.state-icon {
    color: #667eea;
    font-size: 1rem;
}

.state-code {
    font-weight: 700;
    color: #2d3748;
    min-width: 30px;
}

.state-name {
    color: #718096;
    font-size: 0.85rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Customer Count */
.customer-count {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #2d3748;
}

/* Orders Count */
.orders-count {
    font-weight: 600;
    color: #2d3748;
    background: rgba(159, 122, 234, 0.1);
    padding: 4px 10px;
    border-radius: 20px;
    display: inline-block;
    text-align: center;
    min-width: 50px;
}

/* Revenue Cell */
.revenue-cell {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #276749;
}

/* AOV Cell */
.aov-cell {
    display: flex;
    justify-content: flex-start;
}

/* Badge Glass Effect */
.glass-badge {
    padding: 6px 12px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.8rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    min-width: 80px;
    text-align: center;
}

.glass-badge.badge-success {
    background: linear-gradient(135deg, 
        rgba(56, 161, 105, 0.8) 0%, 
        rgba(56, 161, 105, 0.7) 100%);
    color: white;
    border-color: transparent;
}

.glass-badge.badge-warning {
    background: linear-gradient(135deg, 
        rgba(237, 137, 54, 0.8) 0%, 
        rgba(221, 107, 32, 0.7) 100%);
    color: white;
    border-color: transparent;
}

.glass-badge.badge-danger {
    background: linear-gradient(135deg, 
        rgba(245, 101, 101, 0.8) 0%, 
        rgba(229, 62, 62, 0.7) 100%);
    color: white;
    border-color: transparent;
}

/* Table Footer */
.table-footer {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 15px 25px;
    border-top: 1px solid rgba(226, 232, 240, 0.5);
    margin-top: auto;
}

.pagination-info {
    color: #718096;
    font-size: 0.85rem;
    font-weight: 500;
    text-align: center;
}

/* Empty State */
.empty-state {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 40px 20px;
    text-align: center;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.empty-state-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 3rem;
    margin-bottom: 15px;
}

.empty-state h4 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    font-size: 0.9rem;
}

/* Animation Keyframes */
@keyframes cardAppear {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideIn {
    from {
        width: 0;
    }
    to {
        width: 60px;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

/* Responsive Design */
@media (max-width: 992px) {
    .modern-card {
        padding: 20px;
        border-radius: 20px;
    }
    
    .page-title {
        font-size: 1.8rem;
    }
    
    .chart-container-glass,
    .table-container-glass {
        min-height: auto;
    }
    
    .chart-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .stats-summary {
        flex-direction: column;
        gap: 10px;
    }
    
    .stat-summary {
        justify-content: center;
    }
    
    .table-header-section {
        padding: 15px 20px;
    }
    
    .glass-input {
        max-width: 100%;
    }
}

@media (max-width: 768px) {
    .glass-container {
        padding: 10px;
    }
    
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .control-group {
        width: 100%;
        justify-content: space-between;
    }
    
    .glass-select {
        flex: 1;
        max-width: none;
    }
    
    .table-responsive {
        overflow-x: auto;
    }
    
    .modern-table {
        min-width: 600px;
    }
}

/* Scrollbar Styling */
::-webkit-scrollbar {
    width: 10px;
}

::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/StateWiseCustomers.vue